import { SheetWithFormComponent } from '@/components/ui/SheetWithFormComponent'
import { useRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { ErrorComponent } from '@/ui-legacy/ErrorComponent'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { FormComponentLoading } from '@/catalog-apps/components/component-library/FormComponent'

// Shadcn components
import { MultiSelectComboboxField, SelectOption } from '@/components/form-components/MultiSelectCombobox'
import { Avatar, AvatarFallback } from '@/components/shadcn/avatar'
import { Badge } from '@/components/shadcn/badge'
import { Button } from '@/components/shadcn/button'
import { Form } from '@/components/shadcn/form'
import { getInitials } from '@/lib/utils'
import { IconDeleteOutline } from '@icons/IconDeleteOutline'

// Form validation schema
const formSchema = z.object({
  selectedAdminIds: z.array(z.number()).min(1, 'Please select at least one admin to add'),
})

export type MaintainAppAdminsForm = z.infer<typeof formSchema>

type MessagesApplicationsApplication = RestSchemas['Messages.Applications.Application']
type MessagesApplicationsUser = RestSchemas['Messages.Applications.User']

type MaintainAppAdminsFormProps = {
  onSubmit?: () => void
  sheetTrigger?: React.ReactNode
  sheetTriggerTooltip?: string
  sheetTitle?: string
  sheetDescription?: string
  openInSheet?: boolean
  selectedApp: MessagesApplicationsApplication
}

export const MaintainAppAdminsForm = (props: MaintainAppAdminsFormProps) => {
  const [sheetOpen, setSheetOpen] = useState(false)

  return props.sheetTrigger || props.openInSheet ? (
    <SheetWithFormComponent
      title={props.sheetTitle}
      description={props.sheetDescription}
      trigger={props.sheetTrigger}
      triggerTooltip={props.sheetTriggerTooltip}
      wide
      isOpen={sheetOpen}
      onOpenChange={setSheetOpen}
    >
      <FormComponent {...props} />
    </SheetWithFormComponent>
  ) : (
    <FormComponent {...props} />
  )
}

const FormComponent = (props: MaintainAppAdminsFormProps) => {
  const {
    getApplicationsApiListAllAdmins,
    getApplicationsApiListApplicationAdmins,
    putApplicationsApiAddApplicationAdmin,
    deleteApplicationsApiRemoveApplicationAdmin,
  } = useRestClient()

  const {
    data: allAdmins,
    isPending: allAdminsLoading,
    isError: allAdminsError,
  } = getApplicationsApiListAllAdmins.useQuery()

  const {
    data: currentAppAdmins,
    isPending: currentAppAdminsLoading,
    isError: currentAppAdminsError,
  } = getApplicationsApiListApplicationAdmins.useQuery({
    query: { applicationId: props.selectedApp.applicationId },
  })

  const { mutateAsync: addApplicationAdmin } = putApplicationsApiAddApplicationAdmin.useMutation(undefined, {
    onSuccess: () => {
      getApplicationsApiListApplicationAdmins.invalidateQueries({})
      toast.success('Admins successfully added')
      props.onSubmit?.()
    },
    onError: () => {
      toast.error('Error adding admins')
    },
  })

  const { mutate: deleteApplicationAdmin } = deleteApplicationsApiRemoveApplicationAdmin.useMutation(undefined, {
    onSuccess: () => {
      getApplicationsApiListApplicationAdmins.invalidateQueries({})
      toast.success('Admin successfully removed')
    },
    onError: () => {
      toast.error('Error removing admin')
    },
  })

  const defaultValues = useMemo(() => {
    return {
      selectedAdminIds: [] as number[],
    }
  }, [])

  const form = useForm<MaintainAppAdminsForm>({
    resolver: zodResolver(formSchema),
    defaultValues,
  })

  // Helper function to get user display name
  const getUserDisplayName = (user: MessagesApplicationsUser): string => {
    return user.givenName || user.familyName
      ? `${user.givenName ?? ''} ${user.familyName ?? ''}`.trim()
      : 'Unknown User'
  }

  // Create admin options with disambiguation for duplicate names
  const adminOptions: SelectOption<number>[] = useMemo(() => {
    if (!allAdmins) return []

    // Sort users by family name
    const sortedAdmins = [...allAdmins].sort((a, b) => {
      const aName = a.familyName || ''
      const bName = b.familyName || ''
      return aName.localeCompare(bName)
    })

    // Count name occurrences for disambiguation
    const nameMap: Record<string, number> = {}
    sortedAdmins.forEach((user) => {
      const displayName = getUserDisplayName(user)
      nameMap[displayName] = (nameMap[displayName] || 0) + 1
    })

    // Create options with disambiguation when needed
    return sortedAdmins
      .filter((user) => !currentAppAdmins?.some((admin) => admin.id === user.id)) // Filter out current admins
      .map((user) => {
        const baseName = getUserDisplayName(user)
        const label = nameMap[baseName] > 1 ? `${baseName} (ID: ${user.id})` : baseName

        return {
          value: user.id,
          label,
        }
      })
  }, [allAdmins, currentAppAdmins])

  const handleSubmit = async (data: MaintainAppAdminsForm) => {
    const promises = data.selectedAdminIds.map((adminId) =>
      addApplicationAdmin({
        query: { applicationId: props.selectedApp.applicationId, adminId },
      })
    )
    await Promise.all(promises)
    form.reset() // Reset form after submission
  }

  const handleDeleteAdmin = (adminId: number) => {
    deleteApplicationAdmin({
      query: { applicationId: props.selectedApp.applicationId, adminId },
    })
  }

  if (allAdminsLoading || currentAppAdminsLoading) return <FormComponentLoading />
  if (allAdminsError || currentAppAdminsError) return <ErrorComponent />

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <div className="flex h-full flex-col">
            <div className="space-y-6">
              {/* <div className="text-lg font-semibold">Manage Admins for {props.selectedApp.appName}</div> */}

              <MultiSelectComboboxField
                control={form.control}
                name="selectedAdminIds"
                label="Add Admins"
                options={adminOptions}
                placeholder="Select admins to add..."
                searchPlaceholder="Search admins..."
                emptyMessage="No available admins found."
              />

              {/* Current Admins List */}
            </div>
            <div className="form-action-container">
              <Button type="submit" disabled={adminOptions.length === 0}>
                Add Selected Admins
              </Button>
            </div>
          </div>
        </form>
      </Form>
      {currentAppAdmins && currentAppAdmins.length > 0 && (
        <div className="space-y-4">
          <div className="text-sm font-medium">Current Admins:</div>
          <div className="space-y-2">
            {currentAppAdmins.map((admin) => (
              <AdminCard key={admin.id} admin={admin} onDelete={() => handleDeleteAdmin(admin.id)} />
            ))}
          </div>
        </div>
      )}
    </>
  )
}

// Simple admin card component
const AdminCard = ({ admin, onDelete }: { admin: MessagesApplicationsUser; onDelete: () => void }) => {
  const displayName =
    admin.givenName || admin.familyName ? `${admin.givenName ?? ''} ${admin.familyName ?? ''}`.trim() : 'Unknown User'

  return (
    <div className="flex items-center justify-between rounded-lg border bg-background p-3">
      <div className="flex items-center gap-5">
        <Avatar className="ring-2 ring-background">
          <AvatarFallback className="bg-accent text-sm text-accent-foreground">
            {getInitials(displayName)}
          </AvatarFallback>
        </Avatar>
        <div className="flex items-center gap-2 text-sm">
          <span className="font-medium">{displayName}</span>
          <Badge variant="outline">{admin.id}</Badge>
        </div>
      </div>
      <Button variant="ghost" size="icon" onClick={onDelete}>
        <IconDeleteOutline />
      </Button>
    </div>
  )
}
